import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Box, CenteredContainer, Link, Text } from '@nerdwallet/currency';
import nds from '@nerdwallet/base-styles/classes';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import UniversalContainer, {
  universalContainerPropTypes,
} from '../universal-container';
import { itemsLinks } from './data/brand-data';
import styles from './sticky-header-v2.module.less';

import {
  stringInputSpec,
  boolCheckboxSpec,
  repeatable,
  stringSelectSpec,
  group,
  numberInputSpec,
} from '../../lib/editing-configs';

const stickyHeaderLayoutStyles = {
  BOA2023: 'boa2023',
  SEM: 'sem',
  BOA2024: 'boa2024', // TODO: change when BOA 2024 style is defined
};

const iconPlacementTypes = {
  DEFAULT: 'default',
  ALTERNATE: 'alternate',
};

const defaultAwardsYear = 2023;

const outlinedLinks = (item) => (
  <Box className={styles.stickyHeaderLinkItem}>
    <Link className={styles.link} href={item?.url || '#'}>
      {item?.name}
    </Link>
  </Box>
);

const buttonLinks = (item) => (
  <Box id={item?.name} className={styles.stickyHeaderButtonItem}>
    <Link href={item?.url || '#'}>
      <Text bold size="x-small" component="a" color="neutral-dark">
        {item?.name}
      </Text>
    </Link>
  </Box>
);

const renderContent = (
  menuItems,
  layout,
  centerInParentContainer,
  alignment,
  icon
) => {
  const menuItemsComponent = {
    [stickyHeaderLayoutStyles.BOA2023]: buttonLinks,
    [stickyHeaderLayoutStyles.SEM]: outlinedLinks,
    [stickyHeaderLayoutStyles.BOA2024]: buttonLinks,
  };

  const menuItemsComponentStyle = classNames([
    styles.stickyHeaderItems,
    {
      [styles.stickyHeaderLinkItems]: layout === stickyHeaderLayoutStyles.SEM,
    },
  ]);

  if (centerInParentContainer) {
    return (
      <CenteredContainer
        className={menuItemsComponentStyle}
        data-testid="centered-container-sticky-header-items-container"
      >
        {icon}
        {menuItems?.map(menuItemsComponent[layout])}
      </CenteredContainer>
    );
  }

  return (
    <Box
      className={menuItemsComponentStyle}
      data-testid="sticky-header-items-container"
      style={{ justifyContent: alignment }}
    >
      {icon}
      {menuItems?.map(menuItemsComponent[layout])}
    </Box>
  );
};

const StickyHeader = ({
  sections,
  usePreConfiguredNav,
  iconImageUrl,
  iconLinkUrl,
  addIconToHeader,
  iconImageHeight,
  iconImageWidth,
  iconImageHeightMobile,
  iconImageWidthMobile,
  iconPlacement,
  iconTopMargin,
  iconLeftMargin,
  alignment,
  centerInParent,
  stickyHeaderLayout,
  container,
  awardsYear,
  size,
}) => {
  const menuItems = usePreConfiguredNav
    ? itemsLinks[awardsYear || defaultAwardsYear]
    : sections;
  const stickyHeaderRef = useRef();
  const [stickyToTop, setStickyToTop] = useState(false);

  function onScroll() {
    const shouldStickyOnTop =
      stickyHeaderRef.current.getBoundingClientRect().top <= 0;
    setStickyToTop(shouldStickyOnTop);
  }

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Sticky header should have a higher z-index, this makes editor panel tab hidden
  useEffect(() => {
    const stickyHeaderControl = document.querySelector(
      'div.editor-control__type.editor-control__type__stickyheaderv2'
    );
    if (stickyHeaderControl) {
      // if panel tab is in the page we make it visible over sticky header
      stickyHeaderControl.style.zIndex = 102;
    }
  }, []);

  const iconPlacementStyle =
    !centerInParent && iconPlacement === iconPlacementTypes.DEFAULT
      ? 1
      : 'initial';

  const dimensions = useMemo(
    () =>
      size === 'lg'
        ? { width: iconImageWidth, height: iconImageHeight }
        : { width: iconImageWidthMobile, height: iconImageHeightMobile },
    [
      iconImageHeight,
      iconImageHeightMobile,
      iconImageWidth,
      iconImageWidthMobile,
      size,
    ]
  );

  const renderIcon = useMemo(
    () => (
      <Link
        href={iconLinkUrl}
        style={{ marginTop: iconTopMargin, marginLeft: iconLeftMargin }}
        className={classNames([
          {
            [nds('margin-right-4')]:
              iconPlacement === iconPlacementTypes.ALTERNATE,
          },
        ])}
      >
        <img
          src={iconImageUrl}
          alt="Nerdwallet Logo"
          style={{ height: dimensions.height, width: dimensions.width }}
        />
      </Link>
    ),
    [
      dimensions.height,
      dimensions.width,
      iconImageUrl,
      iconLeftMargin,
      iconLinkUrl,
      iconPlacement,
      iconTopMargin,
    ]
  );

  const outputIconWhenInDefaultPlacement =
    addIconToHeader &&
    iconImageUrl &&
    iconPlacement === iconPlacementTypes.DEFAULT;

  const outputIconWhenInAlternatePlacement =
    addIconToHeader &&
    iconImageUrl &&
    iconPlacement === iconPlacementTypes.ALTERNATE;

  return (
    <Box
      ref={stickyHeaderRef}
      id="sticky-header-v2"
      data-testid="sticky-header-v2"
      className={classNames([
        styles.stickyHeader,
        {
          [styles.disableDropShadow]: !stickyToTop,
        },
      ])}
    >
      <div />
      {outputIconWhenInDefaultPlacement && renderIcon}
      <UniversalContainer {...container} style={{ flex: iconPlacementStyle }}>
        {renderContent(
          menuItems,
          stickyHeaderLayout,
          centerInParent,
          alignment,
          (outputIconWhenInAlternatePlacement && renderIcon) || null,
          awardsYear
        )}
      </UniversalContainer>
      <div />
    </Box>
  );
};

StickyHeader.propTypes = {
  addIconToHeader: PropTypes.bool,
  iconImageUrl: PropTypes.string,
  iconLinkUrl: PropTypes.string,
  iconImageHeight: PropTypes.number,
  iconImageWidth: PropTypes.number,
  iconImageHeightMobile: PropTypes.number,
  iconImageWidthMobile: PropTypes.number,
  iconPlacement: PropTypes.string,
  iconTopMargin: PropTypes.number,
  iconLeftMargin: PropTypes.number,
  awardsYear: PropTypes.string,
  usePreConfiguredNav: PropTypes.bool,
  shouldTrack: PropTypes.bool,
  alignment: PropTypes.string,
  centerInParent: PropTypes.bool,
  stickyHeaderLayout: PropTypes.string,
  container: universalContainerPropTypes,
  size: PropTypes.string,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
    })
  ),
};

StickyHeader.defaultProps = {
  addIconToHeader: false,
  alignment: 'center',
  iconImageUrl: '',
  iconLinkUrl: '',
  iconImageHeight: null,
  iconImageWidth: null,
  iconImageHeightMobile: null,
  iconImageWidthMobile: null,
  iconTopMargin: 0,
  iconLeftMargin: 0,
  iconPlacement: iconPlacementTypes.DEFAULT,
  awardsYear: '2023',
  sections: [],
  usePreConfiguredNav: false,
  shouldTrack: false,
  centerInParent: false,
  stickyHeaderLayout: stickyHeaderLayoutStyles.BOA2023,
};

StickyHeader.getEditingConfig = (componentDocument) => {
  const sectionConfig = !componentDocument?.props?.usePreConfiguredNav
    ? [
        repeatable('sections', 'Sections', 'Section', () => {
          return [
            stringInputSpec('name', 'Name'),
            stringInputSpec('url', 'Section URL'),
          ];
        }),
      ]
    : [];

  const awardsYearConfig = componentDocument?.props?.usePreConfiguredNav
    ? [
        stringSelectSpec('awardsYear', 'Awards Year', [
          { label: '2023', value: '2023' },
          { label: '2024', value: '2024' },
        ]),
      ]
    : [];

  const addIconToHeaderInputConfig = componentDocument?.props?.addIconToHeader
    ? [
        stringInputSpec('iconImageUrl', 'Icon Image Url'),
        stringInputSpec('iconLinkUrl', 'Icon Link Url'),
        numberInputSpec('iconImageWidth', 'Icon Image Width'),
        numberInputSpec('iconImageHeight', 'Icon Image Height'),
        numberInputSpec('iconImageWidthMobile', 'Icon Image Width Mobile'),
        numberInputSpec('iconImageHeightMobile', 'Icon Image Height Mobile'),
        numberInputSpec('iconTopMargin', 'Icon Top Margin'),
        numberInputSpec('iconLeftMargin', 'Icon Left Margin'),
        stringSelectSpec('iconPlacement', 'Icon Placement', [
          { label: 'Default', value: iconPlacementTypes.DEFAULT },
          { label: 'Alternate', value: iconPlacementTypes.ALTERNATE },
        ]),
      ]
    : [];

  return [
    boolCheckboxSpec('usePreConfiguredNav', 'Use pre-configured navigation?'),
    ...awardsYearConfig,
    stringSelectSpec('stickyHeaderLayout', 'Header button style', [
      { label: 'BOA 2023 style', value: stickyHeaderLayoutStyles.BOA2023 },
      { label: 'SEM page style', value: stickyHeaderLayoutStyles.SEM },
      { label: 'BOA 2024 style', value: stickyHeaderLayoutStyles.BOA2024 },
    ]),
    group('Container', [
      boolCheckboxSpec('centerInParent', 'Center In Parent?'),
      ...UniversalContainer.getEditingConfig(
        componentDocument,
        'Container',
        ['hide'],
        'container',
        {
          disableGroup: true,
        }
      ),
    ]),
    group('Header Settings', [
      boolCheckboxSpec('addIconToHeader', 'Add icon to header?'),
      ...addIconToHeaderInputConfig,
      stringSelectSpec(
        'alignment',
        'Alignment',
        [
          { label: 'Center', value: 'center' },
          { label: 'Left', value: 'flex-start' },
          { label: 'Right', value: 'flex-end' },
        ],
        { help: 'Defaults to "Center"' }
      ),
    ]),
    ...sectionConfig,
  ];
};

StickyHeader.displayName = 'StickyHeader';

export default connect(
  createStructuredSelector({
    size: (state) => state?.mediaQuery,
  })
)(StickyHeader);
